import { useState } from 'react';
import type { Mashup, NameScores } from '../store/sessionStore';

interface ScoringTableProps {
  mashups: Mashup[];
  onRemove: (id: string) => void;
  onToggleShortlist: (id: string) => void;
  shortlisted: string[];
}

const SCORE_LABELS: Record<keyof NameScores, string> = {
  conceptFit: 'Concept Fit',
  memorability: 'Memorability',
  pronounceability: 'Pronounce',
  uniqueness: 'Uniqueness',
  trademarkRisk: 'Trademark',
  domainAvailability: 'Domain',
  searchDiscoverability: 'SEO',
  emotionalResonance: 'Emotion',
  extendability: 'Extend',
  visualAdaptability: 'Visual'
};

const SCORE_DESCRIPTIONS: Record<keyof NameScores, string> = {
  conceptFit: 'Concept & Tone Fit',
  memorability: 'Memorability',
  pronounceability: 'Pronounceability & Spellability',
  uniqueness: 'Uniqueness & Differentiation',
  trademarkRisk: 'Trademark & Legal Risk (5 = low risk)',
  domainAvailability: 'Domain & Handle Availability',
  searchDiscoverability: 'Search & Discoverability',
  emotionalResonance: 'Emotional Resonance',
  extendability: 'Conceptual Extendability',
  visualAdaptability: 'Visual & Linguistic Adaptability'
};

function getScoreColor(score: number): string {
  if (score >= 4) return '#22c55e';
  if (score >= 3) return '#eab308';
  return '#ef4444';
}

export const ScoringTable = ({ mashups, onRemove, onToggleShortlist, shortlisted }: ScoringTableProps) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const sortedMashups = [...mashups].sort((a, b) => {
    const scoreA = a.evaluation?.totalScore ?? 0;
    const scoreB = b.evaluation?.totalScore ?? 0;
    return scoreB - scoreA;
  });

  const hasEvaluations = mashups.some(m => m.evaluation);

  if (!hasEvaluations) {
    return (
      <div className="scoring-table-empty">
        <p>Scoring data will appear here once evaluation is complete.</p>
      </div>
    );
  }

  return (
    <div className="scoring-table-container">
      <table className="scoring-table">
        <thead>
          <tr>
            <th className="name-column">Name</th>
            <th className="score-column">Total</th>
            {Object.entries(SCORE_LABELS).map(([key, label]) => (
              <th key={key} className="score-column" title={SCORE_DESCRIPTIONS[key as keyof NameScores]}>
                {label}
              </th>
            ))}
            <th className="actions-column">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedMashups.map((mashup) => {
            const isExpanded = expandedRow === mashup.id;
            const isShortlisted = shortlisted.includes(mashup.id);
            const evaluation = mashup.evaluation;

            return (
              <>
                <tr key={mashup.id} className={isShortlisted ? 'shortlisted' : ''}>
                  <td className="name-cell">
                    <button
                      type="button"
                      className="expand-button"
                      onClick={() => setExpandedRow(isExpanded ? null : mashup.id)}
                    >
                      {isExpanded ? '▼' : '▶'}
                    </button>
                    <span className="name-text">{mashup.label}</span>
                  </td>
                  <td className="total-score">
                    {evaluation ? (
                      <strong style={{ color: getScoreColor(evaluation.totalScore / 20) }}>
                        {evaluation.totalScore}
                      </strong>
                    ) : (
                      <span className="no-score">—</span>
                    )}
                  </td>
                  {Object.keys(SCORE_LABELS).map((key) => {
                    const scoreKey = key as keyof NameScores;
                    const score = evaluation?.scores[scoreKey];
                    return (
                      <td key={key} className="score-cell">
                        {score !== undefined ? (
                          <span
                            className="score-badge"
                            style={{ backgroundColor: getScoreColor(score) }}
                          >
                            {score.toFixed(1)}
                          </span>
                        ) : (
                          <span className="no-score">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="actions-cell">
                    <button
                      type="button"
                      className={isShortlisted ? 'shortlist-button active' : 'shortlist-button'}
                      onClick={() => onToggleShortlist(mashup.id)}
                      title={isShortlisted ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      {isShortlisted ? '★' : '☆'}
                    </button>
                    <button
                      type="button"
                      className="remove-button"
                      onClick={() => onRemove(mashup.id)}
                      title="Remove"
                    >
                      ×
                    </button>
                  </td>
                </tr>
                {isExpanded && evaluation && (
                  <tr className="expanded-row">
                    <td colSpan={13}>
                      <div className="evaluation-details">
                        <div className="evaluation-section">
                          <h4>Strengths</h4>
                          <ul>
                            {evaluation.strengths.map((strength, i) => (
                              <li key={i}>{strength}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="evaluation-section">
                          <h4>Risks</h4>
                          <ul>
                            {evaluation.risks.map((risk, i) => (
                              <li key={i}>{risk}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="evaluation-section">
                          <h4>Notes</h4>
                          <p>{evaluation.notes}</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
